import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { MAX_ACTIVE_KNOWLEDGE_PER_TYPE, MAX_PROMPT_LENGTH } from "~/constants/knowledge";
import { TOAST_MESSAGES } from "~/constants/messages";
import { KnowledgeApi } from "~/functions/api/knowledge";
import type { CreateKnowledge, Knowledge } from "~/interface/knowledge";
import type { RootState } from "~/store";
import styles from "./CreateKnowledgeModal.module.scss";

// 現在の日時をISO文字列として取得
const getCurrentDateTime = (): string => {
  const now = new Date();
  const isoString = now.toISOString();
  return isoString;
};

// Unixタイムスタンプを日付に変換するユーティリティ
const parseTimestamp = (timestamp: string | number): Date | null => {
  try {
    // 文字列の場合は数値に変換
    const numericTimestamp = typeof timestamp === "string" ? parseFloat(timestamp) : timestamp;

    // Unixタイムスタンプ（秒単位）の場合
    if (numericTimestamp > 1000000000 && numericTimestamp < 10000000000) {
      const date = new Date(numericTimestamp * 1000);
      return date;
    }

    // ミリ秒単位のタイムスタンプの場合
    if (numericTimestamp > 1000000000000) {
      const date = new Date(numericTimestamp);
      return date;
    }

    // ISO文字列として試行
    const date = new Date(timestamp);
    if (!Number.isNaN(date.getTime())) {
      return date;
    }

    console.error("Could not parse timestamp:", timestamp);
    return null;
  } catch (error) {
    console.error("Error parsing timestamp:", error);
    return null;
  }
};

export interface CreateKnowledgeModalRef {
  submit: () => void;
  cancel: () => void;
}

interface Props {
  onKnowledgeCreated?: (newKnowledge: Knowledge) => void;
  onClose?: () => void;
  onToggleActive?: (knowledge: Knowledge) => Promise<void>;
  selectedType?: CreateKnowledge["type"];
  editingKnowledge?: Knowledge | null;
  isEditMode?: boolean;
  showActiveToggle?: boolean; // 有効化トグルを表示するかどうか
  onStateChange?: (state: { canSubmit: boolean; isSubmitting: boolean }) => void;
  allKnowledges?: Knowledge[]; // 有効化数チェック用
}

const CreateKnowledgeModal = forwardRef<CreateKnowledgeModalRef, Props>(
  (
    {
      onKnowledgeCreated,
      onClose,
      onToggleActive,
      selectedType,
      editingKnowledge,
      isEditMode = false,
      showActiveToggle: _showActiveToggle = false,
      onStateChange,
      allKnowledges = [],
    },
    ref,
  ) => {
    const [title, setTitle] = useState("");
    const [prompt, setPrompt] = useState("");
    const [type, setType] = useState<CreateKnowledge["type"]>(selectedType || "system-proposal");
    const [enabled, setEnabled] = useState(true);
    const [isLock, setIsLock] = useState(false);

    const { currentUser, currentProject } = useSelector((state: RootState) => state.auth) as any;
    const knowledgeApi = new KnowledgeApi();

    // 編集モードの場合、初期値を設定
    useEffect(() => {
      if (isEditMode && editingKnowledge) {
        setTitle(editingKnowledge.title);
        setPrompt(editingKnowledge.meta_prompt);
        setType(editingKnowledge.type);
        setEnabled(editingKnowledge.isActive ?? true); // 有効状態も設定
      }
    }, [isEditMode, editingKnowledge]);

    const handleSubmit = async () => {
      if (
        !title ||
        !prompt ||
        !currentUser?.id ||
        !currentUser?.organization_id ||
        !currentProject?.id
      ) {
        toast.error(TOAST_MESSAGES.KNOWLEDGE.REQUIRED_FIELDS);
        return;
      }

      const action = isEditMode ? "Updating" : "Creating";

      setIsLock(true);
      try {
        let result;

        if (isEditMode && editingKnowledge) {
          // 編集モード
          const currentDate = getCurrentDateTime();
          const updatedKnowledge: Knowledge = {
            ...editingKnowledge,
            type,
            title,
            meta_prompt: prompt,
            modified_at: currentDate,
          };

          const updateResult = await knowledgeApi.updateMetaPrompt(
            currentUser.organization_id,
            currentProject.id,
            editingKnowledge.id,
            updatedKnowledge,
          );

          // APIが成功した場合（trueが返された場合）
          if (updateResult === true) {
            // 更新されたデータを作成（ローカルの更新データを使用）
            result = {
              ...updatedKnowledge,
              isActive: enabled,
            };
          } else {
            result = null;
          }
        } else {
          // 作成モード
          const currentDate = getCurrentDateTime();
          const createData: CreateKnowledge = {
            type,
            title,
            meta_prompt: prompt,
            created_at: currentDate,
          };

          result = await knowledgeApi.create(
            currentUser.organization_id,
            currentProject.id,
            createData,
          );

          // APIレスポンスのタイムスタンプを正しい日付形式に変換
          if (result?.created_at) {
            const parsedDate = parseTimestamp(result.created_at);
            if (parsedDate) {
              result.created_at = parsedDate.toISOString();
            }
          }

          // APIレスポンスのmodified_byにcurrentUser.display_nameを設定
          if (result) {
            result.modified_by = currentUser.display_name;
          }
        }

        if (result) {
          if (isEditMode) {
            // 編集モードの場合は従来通り
            toast.success(TOAST_MESSAGES.KNOWLEDGE.UPDATED);
            if (onKnowledgeCreated) {
              onKnowledgeCreated(result);
            }
          } else {
            // 新規作成後、自動的に有効化を試みる
            toast.success(TOAST_MESSAGES.KNOWLEDGE.CREATED);

            // 有効化数の上限チェック
            const activeCount = allKnowledges.filter(
              (k) => k.type === result.type && k.isActive,
            ).length;

            if (activeCount >= MAX_ACTIVE_KNOWLEDGE_PER_TYPE) {
              // 上限に達している場合はトーストでエラー表示し、保存のみ行う
              toast.error(
                `このドキュメントタイプの有効化数は最大${MAX_ACTIVE_KNOWLEDGE_PER_TYPE}件までです。ナレッジは保存されましたが、有効化されていません。`,
              );
              if (onKnowledgeCreated) {
                onKnowledgeCreated(result);
              }
            } else {
              // 上限に達していない場合は自動的に有効化
              if (onToggleActive) {
                try {
                  await onToggleActive(result);
                  if (onKnowledgeCreated) {
                    onKnowledgeCreated({ ...result, isActive: true });
                  }
                } catch (error) {
                  console.error("Failed to activate knowledge:", error);
                  toast.error(TOAST_MESSAGES.KNOWLEDGE.ACTIVATE_AFTER_SAVE_FAILED);
                  if (onKnowledgeCreated) {
                    onKnowledgeCreated(result);
                  }
                }
              } else {
                // onToggleActiveが提供されていない場合は保存のみ
                if (onKnowledgeCreated) {
                  onKnowledgeCreated(result);
                }
              }
            }

            // フォームをリセット
            setTitle("");
            setPrompt("");
            setType(selectedType || "system-proposal");
          }
        } else {
          const errorMessage = isEditMode ? "更新に失敗しました" : "作成に失敗しました";
          toast.error(errorMessage);
        }
      } catch (error) {
        console.error(`Failed to ${action.toLowerCase()} knowledge:`, error);
        const errorMessage = isEditMode
          ? "更新中にエラーが発生しました"
          : "作成中にエラーが発生しました";
        toast.error(errorMessage);
      } finally {
        setIsLock(false);
      }
    };

    const handleCancel = () => {
      if (onClose) {
        onClose();
      }
    };

    // 親（モーダルのフッター）へ状態提供
    useEffect(() => {
      const canSubmit = !isLock && !!title && !!prompt;
      onStateChange?.({ canSubmit, isSubmitting: isLock });
    }, [title, prompt, isLock, onStateChange]);

    useImperativeHandle(ref, () => ({
      submit: handleSubmit,
      cancel: handleCancel,
    }));

    return (
      <div className={styles["modalContent"]}>
        <div className={styles["wrapper"]}>
          <div className={styles["formGroup"]}>
            <div className={styles["labelRow"]}>
              <label>タイトル</label>
              <div className={styles["characterCount"]}>{title.length} / 100字</div>
            </div>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className={styles["inputField"]}
              maxLength={100}
              placeholder="タイトルを入力"
              disabled={isLock}
              data-testid="knowledge-title"
            />
          </div>
          <div className={styles["formGroup"]}>
            <div className={styles["labelRow"]}>
              <label>プロンプト</label>
              <div className={styles["characterCount"]}>
                {prompt.length} / {MAX_PROMPT_LENGTH}字
              </div>
            </div>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              className={styles["textareaField"]}
              rows={6}
              maxLength={MAX_PROMPT_LENGTH}
              placeholder="プロンプト内容を入力"
              disabled={isLock}
              data-testid="knowledge-description"
            />
          </div>
        </div>
      </div>
    );
  },
);

export default CreateKnowledgeModal;
